import { useFileSelection } from '@/hooks/useFileSelection';
import { alert } from '@/services/alert';
import { uploadAttachments } from '@/services/attachments';
import { fetchBot } from '@/services/services';
import { CommunicationThreadTopic } from '@bariendo/lib/constants';
import { useDisclosure } from '@mantine/hooks';
import { createReference } from '@medplum/core';
import { useMedplum } from '@medplum/react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTimelineContext } from './useTimelineContext';

export function useSendChatMessage() {
  const { patientContext, messageBoxContext } = useTimelineContext();
  const patient = patientContext?.patient;
  const { setShowMessageInput, autofocusMessageBox, setAutofocusMessageBox, setSentMessage } = messageBoxContext;

  const fileInputRef = useRef<HTMLButtonElement>(null);
  const [message, setMessage] = useState('');
  const [richTextContent, setRichTextContent] = useState<any>({});
  const [isSending, setIsSending] = useState(false);
  const medplum = useMedplum();
  const { files, handleSelectFiles, removeFile, clearFiles } = useFileSelection({
    maxAttachments: null,
    maxBundleSizeBytes: null,
  });
  const [pdfModalOpened, { open: openPdfModal, close: closePdfModal }] = useDisclosure(false);
  const [selectedPdfFile, setSelectedPdfFile] = useState<File | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const textareaInputRef = useRef<HTMLTextAreaElement | HTMLDivElement>(null);

  const patientRef = useMemo(() => (patient ? createReference(patient) : undefined), [patient]);

  // Clear message state when patient changes
  useEffect(() => {
    setMessage('');
    setRichTextContent({});
  }, [patient?.id]);

  useEffect(() => {
    if (!autofocusMessageBox) return;
    window.scrollTo({ top: 0, behavior: 'smooth' });
    const timeoutId = setTimeout(() => {
      textareaInputRef.current?.focus();
      setAutofocusMessageBox(false);
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [autofocusMessageBox, setAutofocusMessageBox]);

  const handleAttachClick = () => {
    fileInputRef.current?.click();
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleSendMessage = async (messageTopic: CommunicationThreadTopic) => {
    if (isSending) return;

    try {
      setIsSending(true);
      const attachments = await uploadAttachments(medplum, files);
      await fetchBot(medplum, 'send-chat-message', {
        to: patientRef,
        topic: messageTopic,
        message,
        richTextContent: JSON.stringify(richTextContent),
        attachments,
      });
      clearFiles();
      setMessage('');
      setRichTextContent({});
      setShowMessageInput(false);
    } catch (error) {
      alert.error(`Couldn't send chat message: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsSending(false);
      setSentMessage(true);
    }
  };

  const handlePdfPreview = (file: File) => {
    setSelectedPdfFile(file);
    const url = URL.createObjectURL(file);
    setPdfUrl(url);
    openPdfModal();
  };

  const handleClosePdfModal = () => {
    closePdfModal();
    if (pdfUrl) {
      URL.revokeObjectURL(pdfUrl);
      setPdfUrl(null);
    }
    setSelectedPdfFile(null);
  };

  useEffect(() => {
    return () => {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [pdfUrl]);

  return {
    textareaInputRef,
    isSending,
    message,
    setMessage,
    richTextContent,
    setRichTextContent,
    handleSendMessage,
    fileInputRef,
    files,
    handleSelectFiles,
    removeFile,
    handleAttachClick,
    pdfModalOpened,
    handlePdfPreview,
    handleClosePdfModal,
    selectedPdfFile,
    pdfUrl,
    formatFileSize,
  };
}
